import React from 'react';

// Canonical primary CTA — replaces ad-hoc `bg-acid text-gray-900 …` shapes
// scattered across the app. Sizes (sm/md/lg) cover modal action rows,
// inline page actions, and hero CTAs. Pass `block` for flex-1 / full-width
// inside action rows. Anything else (extra spacing, icons, layout) goes
// through `className` — kept additive so call sites can still customise.
//
// `as` lets the same shape render as a router `<Link>` (or any element)
// when the CTA is a navigation rather than an action. Keeps deep-linking
// and right-click-open-in-new-tab without us having to split the API.
const SIZE = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export const PrimaryButton = React.forwardRef(function PrimaryButton(
  { as: Component = 'button', size = 'md', block = false, className = '', type, children, ...rest },
  ref,
) {
  // `inline-flex items-center justify-center gap-2` is intentional — it's
  // a no-op for single-text buttons and lets arrow/icon CTAs sit on the
  // baseline without each call site re-declaring the layout.
  // `focus-visible:` ring is the WCAG 2.4.7 fix — keyboard-only users
  // had no visible focus indicator on the canonical primary CTA across
  // the whole app. The ring colour matches the brand acid; offset puts
  // it outside the button so it's visible on any background.
  const base = 'inline-flex items-center justify-center gap-2 bg-acid text-gray-900 font-semibold rounded-lg hover:bg-acid-400 disabled:opacity-50 disabled:cursor-not-allowed transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acid focus-visible:ring-offset-2 focus-visible:ring-offset-night';
  const width = block ? 'flex-1' : '';
  // Only meaningful when rendering an actual <button>; pass-through is
  // dropped for Link / anchor variants where `type` doesn't apply.
  const typeProp = Component === 'button' ? { type: type || 'button' } : {};
  return (
    <Component
      ref={ref}
      className={`${base} ${SIZE[size] || SIZE.md} ${width} ${className}`.trim()}
      {...typeProp}
      {...rest}
    >
      {children}
    </Component>
  );
});
